import React, { useEffect } from "react";

import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import { activeBorderBottom } from "../../activeLink";
import NewInput from "../../forms/NewInput";
import Markdown from "../../Markdown";
import Button from "../../NewForms/Button";
import { Tab, TabbedControlled } from "../../Tabs";
import HeadlineItem from "../ContentElement/HeadlineItem";
import { SideNavContainer } from "../SideNavContainer";
import useSideNavStore from "../SideNavContainer/store";
import useLngCalculatorStore from "./store";

const Container = styled.div``;

const ContentContainer = styled.div`
    ${({ theme }) => css`
        ${theme.paddings.containerOuter.right}
        ${theme.paddings.containerOuter.left}
    `}
`;

const StyledTabbed = styled(TabbedControlled)`
    margin-bottom: 40px;

    ${Tab} {
        font-size: 2rem;
        font-weight: 900;
        ${activeBorderBottom({ size: "8px" })}
        ${({ theme }) =>
            theme.breakpoints.upTo.extraSmall.css`
          font-size: 1.5rem;`}
    }
`;

const Headline = styled(HeadlineItem)`
    order: 1;
    ${({ theme }) => css`
        ${theme.breakpoints.from.medium.css`
             order: 2;
        `}
    `}
`;

const Text = styled(Markdown)`
    font-size: 1em;
    ${({ theme }) => css`
        color: ${theme.colors.blue.toString()};
    `};
`;

const Grid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    ${({ theme }) => css`
        ${theme.breakpoints.from.medium.css`
              display: grid;
              grid-template-columns: 1fr 1fr;
              column-gap: 30px;
              @media (min-width: 970px) and (max-width: 1350px) {
                  display: flex;
                  flex-direction: column;
                  gap: 10px;
              }
            `}
    `};
`;

const TextContainer = styled.div`
    order: 2;
    ${({ theme }) => css`
        ${theme.breakpoints.from.medium.css`
             order: 3;
        `}
    `}
`;

const CalculatorContainer = styled.div`
    ${({ theme }) => css`
        background-color: ${theme.colors.grey.toString()};
    `};

    padding: 20px 30px;
    border-radius: 20px;
    order: 3;
    grid-row: 1 / span 2;
    ${({ theme }) => css`
        ${theme.breakpoints.from.medium.css`
             order: 1;
        `}
    `}
`;

const InputWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    ${({ theme }) => css`
        color: ${theme.colors.blue.toString()};
    `};
    ${({ theme }) => css`
        ${theme.breakpoints.from.medium.css`
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
        `}
    `}
`;

const Error = styled.span`
    ${({ theme }) => css`
        color: ${theme.colors.red.toString()};
    `};
`;

const LngCalculator = ({ name, text }) => {
    const theme = useTheme();
    const [paddingLeft] = useSideNavStore((state) => [state.paddingLeft]);
    const [
        tap,
        input,
        result,
        setTap,
        calculate,
        setInput,
        addError,
        clearError,
        hasErrorAt,
        hasError,
    ] = useLngCalculatorStore((state) => [
        state.tap,
        state.input,
        state.result,
        state.setTap,
        state.calculate,
        state.setInput,
        state.addError,
        state.clearError,
        state.hasErrorAt,
        state.hasError,
    ]);

    useEffect(() => {
        if (input.inputHeatingOil || input.inputGas) calculate();
    }, [tap]);

    const handleChange = (event) => {
        setInput({
            ...input,
            ...{ [event.target.name]: event.target.value },
        });
    };

    return (
        <Container>
            <ContentContainer>
                <SideNavContainer paddingLeft={paddingLeft}>
                    <Grid>
                        <CalculatorContainer>
                            <StyledTabbed
                                onTabSelect={setTap}
                                tabs={{
                                    heatingOil: "Heizölverbrauch",
                                    gas: "Erdgasverbrauch",
                                }}
                            >
                                {{
                                    heatingOil: (
                                        <InputWrapper>
                                            <div>
                                                <NewInput
                                                    placeholder="Heizölverbrauch in Liter"
                                                    name="inputHeatingOil"
                                                    onChange={handleChange}
                                                    value={
                                                        input.inputHeatingOil
                                                    }
                                                    validation="decimalNumber"
                                                    forceError={hasErrorAt(
                                                        "input.inputHeatingOil"
                                                    )}
                                                    onError={() =>
                                                        addError(
                                                            "input.inputHeatingOil"
                                                        )
                                                    }
                                                    onErrorSolved={() =>
                                                        clearError(
                                                            "input.inputHeatingOil"
                                                        )
                                                    }
                                                    withField
                                                />
                                                {hasError && (
                                                    <Error>
                                                        Bitte gebe eine positive
                                                        Zahl ein
                                                    </Error>
                                                )}
                                            </div>
                                            <Button onClick={() => calculate()}>
                                                Flüssiggasmenge berechnen
                                            </Button>
                                            <div>
                                                entspricht{" "}
                                                <span
                                                    css={css`
                                                        font-weight: 500;
                                                    `}
                                                >
                                                    {result}
                                                </span>{" "}
                                                Liter Flüssiggas
                                            </div>
                                        </InputWrapper>
                                    ),
                                    gas: (
                                        <InputWrapper>
                                            <div>
                                                <NewInput
                                                    placeholder="Erdgasverbrauch in kWh"
                                                    name="inputGas"
                                                    onChange={handleChange}
                                                    value={input.inputGas}
                                                    validation="decimalNumber"
                                                    forceError={hasErrorAt(
                                                        "input.inputGas"
                                                    )}
                                                    onError={() =>
                                                        addError(
                                                            "input.inputGas"
                                                        )
                                                    }
                                                    onErrorSolved={() =>
                                                        clearError(
                                                            "input.inputGas"
                                                        )
                                                    }
                                                    withField
                                                />
                                                {hasError && (
                                                    <Error>
                                                        Bitte gebe eine positive
                                                        Zahl ein
                                                    </Error>
                                                )}
                                            </div>
                                            <Button onClick={() => calculate()}>
                                                Flüssiggasmenge berechnen
                                            </Button>
                                            <div>
                                                entspricht{" "}
                                                <span
                                                    css={css`
                                                        font-weight: 500;
                                                    `}
                                                >
                                                    {result}
                                                </span>{" "}
                                                Liter Flüssiggas
                                            </div>
                                        </InputWrapper>
                                    ),
                                }}
                            </StyledTabbed>
                        </CalculatorContainer>
                        <div>
                            <Headline
                                headline={3}
                                color={theme.colors.red.toString()}
                            >
                                {name}
                            </Headline>
                            <TextContainer>
                                <Text>{text}</Text>
                            </TextContainer>
                        </div>
                    </Grid>
                </SideNavContainer>
            </ContentContainer>
        </Container>
    );
};

export default LngCalculator;
